/**
 * Notifications API client
 */

import { getAPIClient } from './client';

export interface Notification {
  id: string;
  user_id: string;
  project_id?: string;
  tenant_id?: string;
  type: string;
  severity: 'critical' | 'warning' | 'info';
  title: string;
  message: string;
  action_url?: string;
  context: Record<string, any>;
  channels_sent: string[];
  channels_delivered: string[];
  channels_failed: string[];
  read: boolean;
  read_at?: string;
  created_at: string;
  expires_at?: string;
}

export interface NotificationListResponse {
  notifications: Notification[];
  total: number;
  unread_count: number;
  page: number;
  page_size: number;
}

export interface NotificationListParams {
  read?: boolean;
  type?: string;
  project_id?: string;
  severity?: string;
  page?: number;
  page_size?: number;
}

export interface NotificationPreferences {
  id: string;
  user_id: string;
  project_id?: string;
  channel_preferences: Record<string, any>;
  notification_preferences: Record<string, any>;
  quiet_hours_enabled: boolean;
  quiet_hours_start?: string;
  quiet_hours_end?: string;
  quiet_hours_timezone: string;
  quiet_hours_override_critical: boolean;
  max_notifications_per_hour: number;
  max_notifications_per_day: number;
  digest_enabled: boolean;
  digest_frequency?: string;
  digest_time?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface NotificationPreferencesUpdate {
  channel_preferences?: Record<string, any>;
  notification_preferences?: Record<string, any>;
  quiet_hours_enabled?: boolean;
  quiet_hours_start?: string;
  quiet_hours_end?: string;
  quiet_hours_timezone?: string;
  quiet_hours_override_critical?: boolean;
  max_notifications_per_hour?: number;
  max_notifications_per_day?: number;
  digest_enabled?: boolean;
  digest_frequency?: string;
  digest_time?: string;
  is_active?: boolean;
}

export interface UnreadCountResponse {
  unread_count: number;
}

/**
 * List user notifications
 */
export async function listNotifications(params?: NotificationListParams & { user_id?: string }): Promise<NotificationListResponse> {
  const client = getAPIClient();
  try {
    // Use trailing slash to match FastAPI route definition
    const response = await client.get('/api/notifications/', { params });
    
    // Log EVERYTHING about the response
    if (import.meta.env.DEV) {
      console.log('[notifications API] listNotifications RAW response object:', response);
      console.log('[notifications API] listNotifications response.data:', response.data);
      console.log('[notifications API] listNotifications response.data type:', typeof response.data);
      console.log('[notifications API] listNotifications response.data valueOf:', response.data?.valueOf?.());
      console.log('[notifications API] listNotifications response keys:', Object.keys(response));
      console.log('[notifications API] response.status:', response.status);
      console.log('[notifications API] response.headers:', response.headers);
      console.log('[notifications API] JSON.stringify(response.data):', JSON.stringify(response.data));
    }
    
    // Handle different response structures - axios should put JSON in response.data
    let data = response.data;
    
    // If data is undefined or null, try to get it from response directly
    if (data === undefined || data === null) {
      console.error('[notifications API] response.data is undefined/null! Full response:', {
        response,
        responseType: typeof response,
        responseKeys: Object.keys(response || {}),
        responseString: JSON.stringify(response, null, 2),
      });
      
      // Try to extract from response object directly
      if ((response as any).notifications) {
        data = response as any;
        console.warn('[notifications API] Found notifications on response object directly');
      } else {
        console.error('[notifications API] No data found anywhere in response');
        return { notifications: [], total: 0, unread_count: 0, page: 1, page_size: 10 };
      }
    }
    
    // Ensure all required fields exist
    const result: NotificationListResponse = {
      notifications: Array.isArray(data.notifications) ? data.notifications : [],
      total: typeof data.total === 'number' ? data.total : 0,
      unread_count: typeof data.unread_count === 'number' ? data.unread_count : 0,
      page: typeof data.page === 'number' ? data.page : (params?.page || 1),
      page_size: typeof data.page_size === 'number' ? data.page_size : (params?.page_size || 10),
    };
    
    if (import.meta.env.DEV) {
      console.log('[notifications API] listNotifications FINAL parsed result:', result);
      console.log('[notifications API] notifications count:', result.notifications.length);
    }
    
    return result;
  } catch (error: any) {
    // Return empty result on error instead of throwing
    console.error('[notifications API] Error fetching notifications:', {
      error: error?.message || error,
      response: error?.response?.data,
      status: error?.response?.status,
      params,
    });
    return { notifications: [], total: 0, unread_count: 0, page: 1, page_size: 10 };
  }
}

/**
 * Get notification by ID
 */
export async function getNotification(notificationId: string, user_id?: string): Promise<Notification> {
  const client = getAPIClient();
  const params = user_id ? { user_id } : {};
  const response = await client.get(`/api/notifications/${notificationId}`, { params });
  return response.data;
}

/**
 * Mark notification as read
 */
export async function markNotificationAsRead(notificationId: string, user_id?: string): Promise<Notification> {
  const client = getAPIClient();
  const params = user_id ? { user_id } : {};
  const response = await client.patch(`/api/notifications/${notificationId}/read`, {}, { params });
  return response.data;
}

/**
 * Delete notification
 */
export async function deleteNotification(notificationId: string, user_id?: string): Promise<void> {
  const client = getAPIClient();
  const params = user_id ? { user_id } : {};
  await client.delete(`/api/notifications/${notificationId}`, { params });
}

/**
 * Mark all notifications as read
 */
export async function markAllNotificationsAsRead(user_id?: string): Promise<{ status: string; message: string; count: number }> {
  const client = getAPIClient();
  const params = user_id ? { user_id } : {};
  const response = await client.patch('/api/notifications/read-all', {}, { params });
  return response.data;
}

/**
 * Get unread notification count
 */
export async function getUnreadCount(user_id?: string): Promise<number> {
  const client = getAPIClient();
  const params = user_id ? { user_id } : {};
  try {
    const response = await client.get<UnreadCountResponse>('/api/notifications/unread-count', { params });
    const unreadCount = response.data?.unread_count || 0;
    
    if (import.meta.env.DEV) {
      console.log('[notifications API] getUnreadCount response:', {
        unread_count: unreadCount,
        user_id,
        response_data: response.data,
      });
    }
    
    return unreadCount;
  } catch (error: any) {
    console.error('[notifications API] Error fetching unread count:', {
      error: error?.message || error,
      response: error?.response?.data,
      status: error?.response?.status,
      user_id,
    });
    return 0;
  }
}

/**
 * Get user notification preferences
 */
export async function getNotificationPreferences(projectId?: string): Promise<NotificationPreferences> {
  const client = getAPIClient();
  const url = projectId 
    ? `/api/notifications/preferences/${projectId}`
    : '/api/notifications/preferences';
  
  try {
    const response = await client.get(url);
    
    if (import.meta.env.DEV) {
      console.log('[notifications API] getNotificationPreferences response:', {
        url,
        projectId,
        status: response.status,
        data: response.data,
        dataType: typeof response.data,
        hasData: !!response.data,
      });
    }
    
    // Handle different response structures
    let data = response.data;
    
    // If data is undefined, try to get it from response directly
    if (data === undefined || data === null) {
      console.error('[notifications API] response.data is undefined/null!', {
        response,
        responseKeys: Object.keys(response || {}),
        url,
        projectId,
      });
      
      // Try to extract from response object directly
      if ((response as any).id) {
        data = response as any;
        console.warn('[notifications API] Found preferences on response object directly');
      } else {
        throw new Error('No data returned from preferences endpoint');
      }
    }
    
    // Validate that we have the required fields
    if (!data.id && !data.user_id) {
      console.error('[notifications API] Invalid preferences response structure:', data);
      throw new Error('Invalid preferences response structure');
    }
    
    return data;
  } catch (error: any) {
    console.error('[notifications API] Error fetching notification preferences:', {
      error: error?.message || error,
      response: error?.response?.data,
      status: error?.response?.status,
      url,
      projectId,
    });
    throw error;
  }
}

/**
 * Update user notification preferences
 */
export async function updateNotificationPreferences(
  preferences: NotificationPreferencesUpdate,
  projectId?: string
): Promise<NotificationPreferences> {
  const client = getAPIClient();
  const url = projectId 
    ? `/api/notifications/preferences/${projectId}`
    : '/api/notifications/preferences';
  const response = await client.put(url, preferences);
  return response.data;
}

/**
 * Test notification channels
 */
export interface TestEmailRequest {
  email: string;
  message?: string;
}

export interface TestSMSRequest {
  phone_number: string;
  message?: string;
}

export interface TestWhatsAppRequest {
  phone_number: string;
  message?: string;
}

export interface TestInAppRequest {
  message?: string;
}

export async function testEmailChannel(request: TestEmailRequest): Promise<{ status: string; message: string }> {
  const client = getAPIClient();
  const response = await client.post('/api/notifications/test/email', request);
  return response.data;
}

export async function testSMSChannel(request: TestSMSRequest): Promise<{ status: string; message: string; external_id?: string }> {
  const client = getAPIClient();
  const response = await client.post('/api/notifications/test/sms', request);
  return response.data;
}

export async function testWhatsAppChannel(request: TestWhatsAppRequest): Promise<{ status: string; message: string; external_id?: string }> {
  const client = getAPIClient();
  const response = await client.post('/api/notifications/test/whatsapp', request);
  return response.data;
}

export async function testInAppChannel(request: TestInAppRequest): Promise<{ status: string; message: string; notification_id: string }> {
  const client = getAPIClient();
  const response = await client.post('/api/notifications/test/inapp', request);
  return response.data;
}

export interface ChannelStatus {
  email: { enabled: boolean; provider: string; configured: boolean };
  sms: { enabled: boolean; provider: string; configured: boolean };
  whatsapp: { enabled: boolean; provider: string; configured: boolean };
  inapp: { enabled: boolean; provider: string; configured: boolean };
}

export async function getChannelStatus(): Promise<ChannelStatus> {
  const client = getAPIClient();
  const response = await client.get('/api/notifications/test/status');
  return response.data;
}

